/* eslint-disable complexity */
import { useState } from 'react';

import {
  useDynamicScript,
  useRemoteInitialisation,
  isRemoteIntialised,
} from '@mindtickle/ui-shell-core/DynamicRemote';
import { DELAYS } from '@mindtickle/ui-shell-core/tti';
import { noop } from '@mindtickle/utils';

export type TRemote = {
  url: string;
  remoteName: string;
  moduleName: string;
};

export type TRemoteModuleDetails = {
  remote: TRemote;
  shouldFailSilently?: boolean;
  maxDelay: DELAYS;
  lazy?: boolean;
  resolveModuleDetailsPromise?: boolean;
};

export const ERRORS = {
  FAILED_TO_LOAD: 'FAILED_TO_LOAD',
  NO_REMOTE_SPECIFIED: 'NO_REMOTE_SPECIFIED',
};

const useFetchRemoteModuleDetails = ({
  remote,
  lazy = true,
  maxDelay = DELAYS.L,
  shouldFailSilently = false,
  resolveModuleDetailsPromise = false,
}: TRemoteModuleDetails) => {
  // TODO: add this to enable performance logging
  // could not be added currently, because we will have to update ui-shell-core and we don't want to do that
  // due to some snags - contact Rahul Sharma for more details
  // const { onLoad, onModuleEvaluation } = usePerformanceLoggerForRemote(remote);

  const [moduleDetails, setModuleDetails] = useState(),
    [error, setError] = useState();

  const { ready: isManifestReady, failed } = useDynamicScript({
    url: remote?.url,
    alreadyInitialised: isRemoteIntialised(remote.remoteName),
    onLoad: noop,
  });

  const { isReadyForRendering, moduleDetails: moduleDetailsPromise } = useRemoteInitialisation(
    remote,
    isManifestReady,
    lazy,
    maxDelay,
    shouldFailSilently
  );

  if (!remote) {
    return { ...moduleDetailsPromise, error: ERRORS.NO_REMOTE_SPECIFIED, loading: false };
  }

  if (failed) {
    if (shouldFailSilently) return { ...moduleDetailsPromise, loading: false };
    return { ...moduleDetailsPromise, error: ERRORS.FAILED_TO_LOAD, loading: false };
  }

  if (!isManifestReady || !isReadyForRendering) {
    return { ...moduleDetailsPromise, error: null, loading: true };
  }

  if (resolveModuleDetailsPromise) {
    moduleDetailsPromise?.module
      .then(_module => setModuleDetails(_module))
      .catch(error => {
        if (!shouldFailSilently) setError(error);
      });
  }

  return {
    ...moduleDetailsPromise,
    error,
    loading: resolveModuleDetailsPromise ? !moduleDetails && !error : false,
    moduleDetails,
  };
};

export default useFetchRemoteModuleDetails;
